import type { AppContext } from "../app/app";
import { GENERIC_PACK, applyPacks, type Pack } from "../data/packs";
import type { ProposedDimension } from "../llm/prompts";
import { el } from "./dom";
import { renderPackChooser, renderProposer } from "./dimension-tools";
import { maybeRequestPersistence } from "./storage";

// True when the store has no purpose yet: no dimensions and no people. This is
// what the empty-state onboarding keys off, so the user is led to choose a
// purpose by doing, not via a blocking wizard.
export function shouldChoosePurpose(dimensionCount: number, peopleCount: number): boolean {
  return dimensionCount === 0 && peopleCount === 0;
}

// The onboarding primary-action label: disabled-at-zero, count-bearing once a
// set is selected. Pure, so the enable/label rule is testable headlessly.
export function startLabel(count: number): string {
  return count === 0 ? "Start" : `Start with ${count} set${count === 1 ? "" : "s"}`;
}

// The value-proposition hero, shown above the chooser in the empty-state
// onboarding only. Pure presentation, within the existing type and palette.
export function renderOnboardingHero(): HTMLElement {
  const benefit = (label: string, text: string) =>
    el(
      "div",
      { class: "benefit" },
      el("span", { class: "label benefit__label", text: label }),
      el("span", { class: "benefit__text", text }),
    );
  return el(
    "div",
    { class: "hero" },
    el(
      "div",
      { class: "hero__head" },
      el("span", { class: "label hero__eyebrow", text: "STRAD · TRUST LENS" }),
      el(
        "h1",
        { class: "hero__headline" },
        "Know who to trust,",
        el("br", { class: "hero__break" }),
        "and on what.",
      ),
    ),
    el(
      "div",
      { class: "hero__body" },
      el(
        "div",
        { class: "hero__col" },
        el("p", {
          class: "lede hero__lead",
          text:
            "Trust Lens is a private notebook for your professional judgement of " +
            "people. Rate the people you work with on the qualities that matter, " +
            "and capture why. When a decision comes up, a manager to back, a " +
            "counterparty to clear, someone to ask, it assembles who you trust " +
            "and what you already know.",
        }),
        el("p", {
          class: "hero__privacy",
          text: "It stays on your device. No score anyone else sees.",
        }),
      ),
      // Order is fixed: Capture, Surface, Assemble (workflow order, the hero
      // feature last). It is a literal list, never sorted.
      el(
        "div",
        { class: "hero__benefits" },
        benefit("Capture", "a read in seconds, while it is fresh."),
        benefit("Surface", "who you lean on, where you are thin."),
        benefit("Assemble", "a briefing for a live call, from your own notes."),
      ),
    ),
  );
}

// One choice, then straight on: pick a pack (or several), describe a role, or
// take the generic set. Each seeds dimensions and calls onSeeded. Reversible by
// design: every path includes the generic fallback, and dimensions stay editable.
export function renderPurposeChooser(
  ctx: AppContext,
  opts: { onSeeded: () => void },
): HTMLElement {
  const root = el("div", { class: "purpose" });

  const choosePacks = async (packs: Pack[]) => {
    await applyPacks(ctx.repo, packs);
    opts.onSeeded();
    // First commitment: ask the browser to keep the data persistently.
    void maybeRequestPersistence(ctx);
  };
  const commitProposed = async (dims: ProposedDimension[]) => {
    // Empty store, so this just seeds the confirmed proposal. Proposed
    // dimensions carry no purpose pack.
    for (const d of await ctx.repo.listDimensions()) {
      await ctx.repo.deleteDimension(d.id);
    }
    for (const d of dims) {
      await ctx.repo.addDimension(d.name, d.definition, d.canonical_key, []);
    }
    opts.onSeeded();
    void maybeRequestPersistence(ctx);
  };

  root.append(
    el("p", {
      class: "lede",
      text:
        "Pick one or more sets tuned to your work; their qualities are combined. " +
        "Dimensions stay editable and you can add more sets later in Settings.",
    }),
    renderPackChooser((packs) => void choosePacks(packs), {
      primaryLabel: startLabel,
      onGeneric: () => void choosePacks([GENERIC_PACK]),
    }),
    el("span", { class: "label", text: "Or describe your own role" }),
    renderProposer(ctx, commitProposed),
  );
  return root;
}
